// ── Cancellation banner, derived from live state ────────────────────────────
//
// BILL-005. The rule lives here, in one place, so the Credits page and anything
// else that needs to answer "is this subscription winding down?" cannot drift
// apart — and so the boundary cases can be tested without rendering a page.
//
// A note on what was actually wrong, because it is easy to mis-diagnose: the
// CMS was ALREADY deriving this from `subscription.cancel_at_period_end` rather
// than from a stored flag. The stale banner was not a frontend caching bug. The
// flag was genuinely still true in the database and at Stripe. The server-side
// fix is what makes it go away; this module exists so the derivation is
// explicit, shared and tested, and so the inverse bug (a real cancellation that
// fails to show) is guarded.

import { isSamePlanName } from './planUtils';
import { daysRemaining } from './dates';

const normalise = (status) => String(status || '').toLowerCase();

// Statuses that mean "this account has a live PAID plan right now".
//
// `trialing` belongs here and its absence is the bug behind the "Scale … FREE
// TRIAL" screenshot: Stripe keeps a subscription in `trialing` while its first
// period is a trial, so a user who upgrades mid-trial holds a real, paid,
// chosen plan under a status that is not the string 'active'. Every check
// written as `status === 'active'` answered "no" and the UI fell through to its
// free-tier wording.
//
// `past_due` is live too — the plan has not lapsed, a payment failed. It gets
// its own label below rather than being called Active.
//
// `pending_downgrade` is live for the same reason, and its absence was the SAME
// omission as `trialing` above, made a second time. A subscription with a
// downgrade queued is running normally; only its future plan is settled. Adding
// it here fixes four derived answers at once, because everything below funnels
// through this set:
//
//   • the date label read "NEXT BILLING" on a cancelled plan, not "PLAN ENDS"
//   • the chip read "Change scheduled" rather than "Set to cancel"
//   • Cancel was offered on a subscription already cancelling
//   • Reactivate was NOT offered — leaving no way to undo the cancellation
//
// A user who cancels and then queues a downgrade lands in exactly this state,
// which is how it reached production unnoticed: each symptom looks like its own
// small bug, and none of them points at a Set literal.
const LIVE_STATUSES = new Set(['active', 'trialing', 'past_due', 'pending_downgrade']);

// The only statuses that mean the plan is not running.
const ENDED_STATUSES = new Set(['cancelled', 'canceled', 'paused']);

/**
 * Is there a paid subscription in force?
 *
 * Stated as "not ended" rather than "in LIVE_STATUSES" on purpose: a
 * subscription ROW is itself the strong signal, since the free tier has none by
 * design. An unrecognised status therefore keeps a paying customer looking like
 * one, instead of falling through to free-tier wording the way the bug did.
 */
export function isLiveSubscription(subscription) {
  if (!subscription) return false;
  return !ENDED_STATUSES.has(normalise(subscription.status));
}

/**
 * Free tier, split into the two states that need different words.
 *
 * ── WHY THIS IS NOT ONE BOOLEAN ─────────────────────────────────────────────
 * "On the free plan" and "on the free trial" are the same plan row. The trial is
 * not a plan and not a subscription — it is `trial_started_at` / `trial_ends_at`
 * on the USER, set to seven days at signup. So a free-tier account is on a trial
 * right up until the clock runs out, and on the Basic plan afterwards, with
 * nothing in the plan data distinguishing the two.
 *
 * Telling them apart matters wherever the difference is stated out loud: an
 * account whose trial has ended and is told it is "on the free trial" is being
 * told the thing it just lost is still running.
 *
 * The rule mirrors the Dashboard's header badge exactly, which is what the
 * Websites tab, the badge and Settings have to agree about.
 *
 * `trial_expired` is the SERVER's answer and is preferred — it is computed
 * against the same clock that voids the credits. The local date compare is the
 * fallback for a session whose payload predates the flag, and is applied only on
 * the free tier: a paying account's trial_ends_at is ancient history and must
 * not be read as an expiry.
 *
 * @param userData the AuthContext user record (`/users/by-email`)
 * @param now      injectable so the boundary is testable
 * @returns {{ onFreePlan: boolean, trialExpired: boolean, onTrial: boolean }}
 */
export function deriveFreeTierState(userData, now = new Date()) {
  const planSlug = String(userData?.plan_slug || '').toLowerCase();
  // A missing slug counts as free: the free plan is the default a new account
  // is created on, so an absent value is far likelier to be that than a paid
  // tier we failed to read.
  const onFreePlan = !planSlug || planSlug === 'free';

  const endsAt = userData?.trial_ends_at ? new Date(userData.trial_ends_at) : null;
  const trialExpired = userData?.trial_expired === true
    || (onFreePlan && !!endsAt && !Number.isNaN(endsAt.getTime())
      && endsAt.getTime() < now.getTime());

  return { onFreePlan, trialExpired, onTrial: onFreePlan && !trialExpired };
}

/**
 * Should the "Cancellation scheduled" banner be shown?
 *
 * Mirrors the server's deriveCancellationBanner. Each clause matters:
 *   - only an ACTIVE subscription can be "scheduled" to cancel; a cancelled one
 *     already has.
 *   - a cancellation with no end date cannot be described honestly.
 *   - a date in the past means the deletion webhook has not landed yet;
 *     "access continues until [yesterday]" is worse than showing nothing.
 *
 * @param now injectable so boundary behaviour can be tested.
 */
export function deriveCancellationBanner(subscription, now = new Date()) {
  const hide = { visible: false, accessUntil: null };
  if (!subscription) return hide;

  // Same LIVE_STATUSES set the rest of this module uses, rather than a second
  // inline copy that can drift from it.
  if (!LIVE_STATUSES.has(normalise(subscription.status))) return hide;

  if (!subscription.cancel_at_period_end) return hide;
  if (!subscription.next_billing_date) return hide;

  const endsAt = new Date(subscription.next_billing_date);
  if (Number.isNaN(endsAt.getTime())) return hide;
  if (endsAt.getTime() <= now.getTime()) return hide;

  return { visible: true, accessUntil: endsAt };
}

/**
 * Should the "Pending Change" / "Scheduled change" notice be shown?
 *
 * The reported sequence — trial → Grow → cancel → reactivate → Optimize →
 * Scale — left three notices on screen at once, two of which contradicted the
 * third: a plan change to Grow, a cancellation, and a plan called Scale. Each
 * clause below refuses one incoherent combination:
 *
 *   - nothing pending: obvious, and the common case.
 *   - already cancelled: a change queued on a subscription that has ended is
 *     not going to happen.
 *   - CANCELLING: the subscription stops at period end, so the change queued
 *     for that same moment never runs. "Your plan will become Grow" and "your
 *     access ends" cannot both be true, and announcing both is what made the
 *     screen unreadable. The cancellation is the one the user must act on.
 *   - pending === current: a "change" to the plan you already hold is not a
 *     change. This is exactly the residue a reactivation implemented as
 *     "change to the same plan" leaves behind.
 *   - a date that has passed: the change should already have applied, so
 *     saying it "will" happen is the same lie as BILL-005's banner promising
 *     access until a date in the past.
 *
 * A MISSING date is not disqualifying — Settings words this as "at period end"
 * and prints no date — but an unparseable one is, so nothing renders as
 * "Invalid Date".
 */
export function derivePendingChange(subscription, now = new Date()) {
  const hide = { visible: false, planId: null, planName: null, effectiveAt: null };
  if (!subscription) return hide;

  const planId = subscription.pending_plan_id ?? null;
  const planName = subscription.pending_plan_name ?? null;
  if (planId == null && !planName) return hide;

  const status = normalise(subscription.status);
  if (status === 'cancelled' || status === 'canceled') return hide;
  // The raw flag, not deriveCancellationBanner: precedence must hold even when
  // the end date is missing and the banner itself cannot be drawn.
  if (subscription.cancel_at_period_end) return hide;

  const sameById = planId != null && subscription.plan_id != null
    && String(planId) === String(subscription.plan_id);
  // Canonical, not literal: the API sends the current plan as "Pro" and the
  // pending one as "Pro Plan", and a raw compare reads those as two plans.
  const sameByName = planId == null && isSamePlanName(planName, subscription.plan_name);
  if (sameById || sameByName) return hide;

  // A scheduled change takes effect at the period end, so next_billing_date is
  // the same moment when the dedicated field is absent. Preferring an explicit
  // date, falling back to the period end, means the notice can almost always
  // name the DAY rather than saying the vaguer "at period end".
  const rawDate = subscription.pending_plan_effective_date || subscription.next_billing_date;
  let effectiveAt = null;
  if (rawDate) {
    const d = new Date(rawDate);
    if (Number.isNaN(d.getTime())) return hide;
    if (d.getTime() <= now.getTime()) return hide;
    effectiveAt = d;
  }

  return { visible: true, planId, planName, effectiveAt };
}

// Chip presentation per status.
//
// Labels are written in SENTENCE case and the surfaces that want capitals apply
// `textTransform: uppercase` in CSS. That is the whole point of them living
// here: Settings said "Cancels at period end" while the Credits tab said
// something else about the same subscription, because each screen kept its own
// table of words. One table, two renderings.
//
// Tone is a name, not a colour: each surface owns its palette, and this module
// has no business knowing hex codes.
/**
 * The chip for a plan whose next plan is already settled.
 *
 * Exported because Settings also uses it for a CANCELLING plan (REQUESTED:
 * "Scale · Change scheduled", with the move to Basic spelled out on its own
 * row). deriveStatusChip still says "Set to cancel" for that state, which the
 * Credits card keeps beside its Cancellation scheduled banner and Reactivate
 * button — so the wording is shared here rather than typed twice.
 */
export const CHANGE_SCHEDULED_LABEL = 'Change scheduled';

const STATUS_CHIPS = {
  active: { key: 'active', label: 'Active', tone: 'positive' },
  trialing: { key: 'trialing', label: 'Active (trial)', tone: 'positive' },
  past_due: { key: 'past_due', label: 'Past due', tone: 'danger' },
  paused: { key: 'paused', label: 'Paused', tone: 'muted' },
  // OLD: label: 'Change scheduled' — same words, now the shared constant above.
  pending_downgrade: { key: 'pending_downgrade', label: CHANGE_SCHEDULED_LABEL, tone: 'warning' },
  cancelled: { key: 'cancelled', label: 'Cancelled', tone: 'danger' },
  canceled: { key: 'cancelled', label: 'Cancelled', tone: 'danger' },
};

/**
 * The status chip for the plan card.
 *
 * THE INVARIANT, and the reported bug in one line: while a subscription row
 * exists, this never returns trial wording. The old expression —
 *
 *   isActivePlan ? 'ACTIVE' : isCancelled ? 'CANCELLED' : trialExpired ? … : 'FREE TRIAL'
 *
 * — was a chain whose final fallback was 'FREE TRIAL', so ANY status outside
 * {active, cancelled} labelled a paid plan as a free trial. `trialing` is such
 * a status, which is how a paying Scale customer was told they were on a free
 * trial.
 *
 * Trial wording belongs to the no-subscription case alone, where it is true.
 */
export function deriveStatusChip(subscription, { trialExpired = false } = {}, now = new Date()) {
  if (!subscription) {
    // ── Once the trial is over the account is ON THE BASIC PLAN ─────────────
    // It is not in a broken or pending state waiting to be resolved: the
    // downgrade already happened, automatically, and Free is a plan the
    // product sells against. The chip answers "what plan am I on?", so after
    // the trial it has to answer "Basic plan" — "Trial ended" answers a
    // different question, about an event now in the past.
    //
    // That event is still on screen where it belongs: the Credits card states
    // the transition in words and dates the day it happened.
    //
    // The tone goes with it. 'warning' orange was right for a status that
    // wanted attention; a plan the account is legitimately on is not a fault,
    // so this is the neutral 'muted' grey.
    return trialExpired
      ? { key: 'free_plan', label: 'Basic plan', tone: 'muted' }
      : { key: 'trial', label: 'Free trial', tone: 'warning' };
  }

  // Winding down outranks the raw status, which stays 'active'/'trialing'
  // throughout the notice period and would otherwise read as untroubled.
  if (deriveCancellationBanner(subscription, now).visible) {
    return { key: 'cancelling', label: 'Set to cancel', tone: 'warning' };
  }

  return STATUS_CHIPS[normalise(subscription.status)]
    || { key: 'active', label: 'ACTIVE', tone: 'positive' };
}

/**
 * The buttons the plan card offers, IN THE ORDER THEY ARE SHOWN.
 *
 * ── WHY AN ORDERED LIST AND NOT THREE BOOLEANS ──────────────────────────────
 * This replaced `deriveSubscriptionActions`, which returned
 * { canManagePlans, canReactivate, canCancel } and left the card to decide the
 * order and the styling. Two things were wrong with that.
 *
 * It could only ever show ONE button, because `canManagePlans` was
 * `!live && !cancelling` — so a running plan offered Cancel and nothing else,
 * and there was no way to change plan without first cancelling.
 *
 * And the emphasis lived in JSX. "Manage Plan is the primary action while the
 * plan runs, and the secondary one while it is winding down" is a single rule
 * about one button; expressed as booleans it becomes two conditionals in the
 * template that can disagree with each other.
 *
 * Order and emphasis are data here, so the card maps over the list and cannot
 * render them in the wrong sequence or promote the wrong one.
 *
 * ── ON SHOWING REACTIVATE AND MANAGE PLAN TOGETHER ──────────────────────────
 * A previous fix made those two mutually exclusive, because a screenshot showed
 * them side by side. That bug was real but it was not "two buttons": it was two
 * independently-broken gates that happened to open at once, rendering two
 * PRIMARIES that said contradictory things — shop for plans, beside your plan
 * is ending.
 *
 * Pairing them deliberately, with Reactivate as the primary and Manage Plan
 * outlined beneath it, says something coherent: undo this, or change what you
 * are undoing it to. The rule worth keeping is not "never two buttons" — it is
 * never two of equal weight, and never an ambiguous primary. Exactly one entry
 * in every list below carries `variant: 'primary'`, and that is asserted.
 *
 * `onBasicPlan` is passed rather than derived: a Basic account and a running
 * trial both have NO subscription row, so nothing in this function's own
 * argument can tell them apart. The caller already knows — it is the same
 * `trialExpired` the status chip and the date label read — and having one
 * answer flow outwards is what stops the card's three halves disagreeing about
 * which state the account is in.
 *
 * @returns Array<{ key, label, variant: 'primary'|'secondary'|'quiet'|'credits' }>
 */
export function derivePlanActions(subscription, now = new Date(), { onBasicPlan = false } = {}) {
  const live = isLiveSubscription(subscription);
  const cancelling = deriveCancellationBanner(subscription, now).visible;

  // Winding down: the offer is to undo it first, and to change plan second —
  // changing plan is also a way to stay, so it is offered rather than hidden.
  if (cancelling) {
    return [
      { key: 'reactivate', label: 'Reactivate Subscription', variant: 'primary' },
      { key: 'manage', label: 'Manage Plan', variant: 'secondary' },
    ];
  }

  // Running: change plan is the main thing anyone comes here to do. Cancelling
  // is deliberately the quietest control on the card — available, not invited.
  if (live) {
    return [
      { key: 'manage', label: 'Manage Plan', variant: 'primary' },
      { key: 'cancel', label: 'Cancel Subscription', variant: 'quiet' },
    ];
  }

  // ── On the Basic plan ────────────────────────────────────────────────────
  // REQUESTED: the two ways to spend money, side by side — a subscription, or
  // a one-off pack. "Choose a Plan" offered only the first and named neither,
  // on the one screen where packs are the equal alternative rather than a
  // consolation.
  //
  // Split from the trial deliberately, even though neither has a subscription
  // row: a trial still gets the single button, because it opens the dialog
  // that explains what happens when the trial ends (see TrialOptionsDialog).
  // A Basic account has already been through that, so it gets the actions
  // themselves rather than the explanation.
  //
  // Exactly one primary, as everywhere else here: 'Get subscription'. Packs
  // are the teal outlined treatment the Add Credits button uses on both this
  // screen and the dashboard, so the same action looks the same everywhere.
  if (onBasicPlan) {
    return [
      { key: 'subscribe', label: 'Get subscription', variant: 'primary' },
      { key: 'buy_packs', label: 'Buy credit pack', variant: 'credits' },
    ];
  }

  // No paid plan and not on Basic: a running trial, or an account whose state
  // we cannot pin down. One offer, and on a trial it opens the dialog that
  // explains what the trial ending does before naming any price.
  return [
    { key: 'choose', label: 'Choose a Plan', variant: 'primary' },
  ];
}


/**
 * The countdown chip on the Credits page.
 *
 * Extracted from CreditManagement because it was a closure inside a very large
 * component, which is precisely why two bugs lived in it unnoticed:
 *
 *   • it never consulted the cancellation state, so a plan that was ENDING
 *     still announced "Next reset in N days" — directly contradicting the
 *     banner above it and the "PLAN ENDS" label beside it;
 *   • it counted with Math.ceil over a millisecond difference, so 29 days and
 *     a few hours displayed as 30.
 *
 * Five branches and no way to test any of them in place. Here they are pure.
 *
 * @param subscription  the live subscription row, or null on the free tier
 * @param cancelling    from deriveCancellationBanner — passed in rather than
 *                      recomputed so the chip and the label cannot disagree
 * @param trialExpired  server flag, with a local date fallback
 * @param trialEnd      effective trial end, used when there is no subscription
 */
export function derivePlanCycleChip({
  subscription = null,
  cancelling = false,
  trialExpired = false,
  trialEnd = null,
} = {}, now = new Date()) {
  // ── Nothing to say about a trial that is over ──────────────────────────────
  // This badge announces the NEXT RESET. Once the trial has ended and the
  // account has moved to Free, "Trial ended" is not a reset — it is an event in
  // the past, and it was the second of two places on this page saying so.
  //
  // The transition is still stated, once, in the sentence under the plan name,
  // which is where it belongs because that is the line that also says what to
  // do about it. A badge and a label repeating it either side of that sentence
  // made a routine, automatic downgrade read as an alarm going off three times.
  //
  // Null rather than a substitute: with no subscription there is no reset date
  // to name, and inventing a reassuring phrase for a date we do not have is
  // worse than showing nothing. The caller hides the badge.
  //
  // Guarded on `!subscription` so this cannot silence a PAYING customer's reset
  // countdown — trialExpired stays true on an account that has since
  // subscribed, and the blanket early return this replaces showed them
  // "Trial ended" in place of their own billing cycle.
  if (trialExpired && !subscription) return null;

  const target = subscription?.next_billing_date || trialEnd || null;

  if (!target) {
    if (!subscription) return 'Trial active';
    return cancelling ? 'Plan ends soon' : 'Next reset soon';
  }

  const days = daysRemaining(target, now);
  if (days == null) return subscription ? 'Next reset soon' : 'Trial active';
  const plural = days === 1 ? '' : 's';

  // A subscription that is winding down does not reset — it ends.
  if (subscription && cancelling) return `Plan ends in ${days} day${plural}`;
  return subscription
    ? `Next reset in ${days} day${plural}`
    : `Trial ends in ${days} day${plural}`;
}

/**
 * The one sentence describing a queued plan change.
 *
 * ── WHY IT IS HERE ──────────────────────────────────────────────────────────
 * derivePendingChange returns structured data and nothing more, so each of the
 * three surfaces that render it composed its own sentence — and they disagreed
 * about the same subscription:
 *
 *   Credits page   "Pending Change: Your subscription changes to Grow on Sep 19, 2026."
 *   Manage plans   "Pending Change: Your subscription changes on 9/19/2026."
 *   Settings       "Moves to Grow on September 19, 2026"
 *
 * The middle one omitted the plan entirely — the single most useful fact —
 * and rendered the date with toLocaleDateString(), which prints US month/day
 * order to a British reader as 9/19/2026 rather than 19/09/2026.
 *
 * One formatter, three call sites, and the wording can only change in one
 * place. The verb is "moves to", which reads as a decision already taken;
 * "changes" left it ambiguous whether the user still had to do something.
 */
/**
 * The pending plan as it should READ — mapped to its display name, with a
 * trailing "Plan" stripped so no sentence says "the Grow Plan plan".
 *
 * Shared because two messages now name the same plan, and a second copy of this
 * is how one of them comes to call a tier something the other does not.
 */
const pendingPlanLabel = (pendingChange, planDisplayName) => {
  const rawName = pendingChange?.planName;
  const name = rawName && planDisplayName ? planDisplayName(rawName) : rawName;
  return name ? String(name).trim().replace(/\s+plan$/i, '') : null;
};

/**
 * The effective date, or null.
 *
 * Explicit en-US with an explicit UTC zone: the effective date is a calendar
 * day, and formatting it in the viewer's zone slides it to the previous day for
 * anyone west of Greenwich.
 */
const pendingWhen = (pendingChange) => (pendingChange?.effectiveAt
  ? pendingChange.effectiveAt.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
  : null);

export function pendingChangeMessage(pendingChange, { planDisplayName } = {}) {
  if (!pendingChange?.visible) return null;

  const plan = pendingPlanLabel(pendingChange, planDisplayName);
  const when = pendingWhen(pendingChange);

  if (plan && when) return `Your subscription moves to the ${plan} plan on ${when}.`;
  if (plan) return `Your subscription moves to the ${plan} plan at the end of your billing period.`;
  if (when) return `Your subscription changes plan on ${when}.`;
  return 'Your subscription changes plan at the end of your billing period.';
}

/** The label that precedes it, kept alongside so the two cannot drift. */
export const PENDING_CHANGE_LABEL = 'Pending change:';

/**
 * What happens once a cancelled plan runs out — the second half of the banner.
 *
 * "Basic" is written out, never passed through getPlanDisplayName: that mapper
 * reads 'Basic' as the PAID entry tier's old name and returns "Grow".
 */
const AFTER_CANCELLATION = 'Afterwards you will be automatically on the free Basic plan.';

/**
 * The one sentence describing a scheduled cancellation.
 *
 * ── WHY IT IS HERE ──────────────────────────────────────────────────────────
 * Same reason as pendingChangeMessage above: deriveCancellationBanner returns
 * structured data, so every surface composed its own sentence. The Credits page
 * said
 *
 *   "Cancellation Scheduled: Access continues until Sep 19, 2026 (America/Denver)."
 *
 * while the dialog shown seconds earlier, on the same action, said "Your
 * Optimize plan stays active until Sep 19, 2026." Manage Plan said nothing at
 * all — a customer could open the plan grid, see their plan sitting there
 * apparently normal, and have no idea it was ending.
 *
 * Two problems with the old wording, beyond the mismatch:
 *   - "Access continues" is vague about WHAT continues. The plan is the thing
 *     the customer bought and the thing that is ending, so it is what the
 *     sentence should name.
 *   - it never named the plan, which is the fact that makes the sentence
 *     actionable on an account that has more than one thing running.
 *
 * ── THE TIMEZONE, WHICH USED TO BE PRINTED ──────────────────────────────────
 * The old banner appended the viewer's IANA zone — "(America/Denver)" — on the
 * grounds that "until Sep 19" is ambiguous by up to a day. That is true, but it
 * bought precision no customer asked for at the cost of a sentence no customer
 * could parse, and the confirmation dialog never did it, so the two disagreed
 * about the same date. The date is a calendar day; it is now stated as one, the
 * same way every other date in this product is.
 */
export function cancellationScheduledMessage(banner, { planName, planDisplayName } = {}) {
  if (!banner?.visible) return null;

  const name = planName && planDisplayName ? planDisplayName(planName) : planName;
  // Strip a trailing "Plan" so the sentence never reads "your Grow Plan plan".
  const plan = name ? String(name).trim().replace(/\s+plan$/i, '') : null;

  const when = banner.accessUntil
    ? banner.accessUntil.toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    })
    : null;

  // `visible` already guarantees a parseable future date (see
  // deriveCancellationBanner), so the date-less branches are defensive only —
  // but a caller passing a hand-built object should still get a sentence.
  // OLD: the four sentences below ended at the date. REQUESTED: say where the
  // account goes next, so "Cancellation scheduled" does not read as the account
  // stopping — it moves to the free Basic plan, automatically.
  //   if (plan && when) return `Your ${plan} plan stays active until ${when}.`;
  //   if (plan) return `Your ${plan} plan stays active until the end of your billing period.`;
  //   if (when) return `Your plan stays active until ${when}.`;
  //   return 'Your plan stays active until the end of your billing period.';
  if (plan && when) return `Your ${plan} plan stays active until ${when}. ${AFTER_CANCELLATION}`;
  if (plan) return `Your ${plan} plan stays active until the end of your billing period. ${AFTER_CANCELLATION}`;
  if (when) return `Your plan stays active until ${when}. ${AFTER_CANCELLATION}`;
  return `Your plan stays active until the end of your billing period. ${AFTER_CANCELLATION}`;
}


/** The label that precedes it, kept alongside so the two cannot drift. */
export const CANCELLATION_SCHEDULED_LABEL = 'Cancellation scheduled:';

/**
 * "You have already scheduled this" — for someone downgrading a second time.
 *
 * ── WHY THIS IS NOT pendingChangeMessage ────────────────────────────────────
 * That one is a NOTICE, read in passing at the top of a screen, and it is
 * phrased about the subscription: "Your subscription moves to the Optimize plan
 * on Sep 19, 2026." This is an ANSWER to an action the customer just took, so
 * it is phrased about their plan and states the schedule as the reason nothing
 * further is going to happen.
 *
 * The distinction matters because of what it replaces. Clicking Downgrade a
 * second time used to walk the customer through the entire quote-and-confirm
 * ceremony and then congratulate them on scheduling a downgrade that was
 * already scheduled — the server re-sent it to Stripe, wrote a second
 * disclosure row, and returned a fresh success dialog. Nothing distinguished
 * that from the first time, so the only honest signal available was the one the
 * screen never gave: you have already done this.
 *
 * Both messages read the same plan name and date through the same two helpers,
 * so they can differ in wording but never in fact.
 */
export function scheduledDowngradeMessage(pendingChange, { planDisplayName } = {}) {
  if (!pendingChange?.visible) return null;

  const plan = pendingPlanLabel(pendingChange, planDisplayName);
  const when = pendingWhen(pendingChange);

  // A missing date is not a missing message — the change is still scheduled,
  // and "at the end of your billing period" is the honest way to say when.
  if (plan && when) return `Your plan is scheduled to move to the ${plan} plan on ${when}.`;
  if (plan) return `Your plan is scheduled to move to the ${plan} plan at the end of your billing period.`;
  if (when) return `Your plan is scheduled to change on ${when}.`;
  return 'Your plan is already scheduled to change at the end of your billing period.';
}
